import { useRef, useState, type ChangeEvent } from "react";
import Button from "@/components/base/Button";
import CameraCaptureModal from "@/components/base/CameraCaptureModal";
import { initials } from "@/utils/format";

interface PhotoUploaderProps {
  /** Image actuelle (URL ou data URL). Vide si aucune photo. */
  value?: string;
  onChange: (value: string) => void;
  lastName: string;
  firstName: string;
  label?: string;
  hint?: string;
  size?: "md" | "lg";
}

/** Fichier accepté avant réduction (photo de téléphone en pleine taille). */
const MAX_SIZE = 15 * 1024 * 1024;
/** Plus grand côté de la photo enregistrée : ~100 Ko en JPEG, net sur les cartes scolaires. */
const MAX_SIDE = 800;

/** Réduit l'image (plus grand côté à MAX_SIDE) et la convertit en JPEG ; fond blanc sous les PNG transparents. */
function shrinkImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("canvas"));
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image"));
    };
    img.src = url;
  });
}

const sizeClasses: Record<NonNullable<PhotoUploaderProps["size"]>, string> = {
  md: "w-16 h-16 text-base",
  lg: "w-20 h-20 text-xl",
};

/**
 * Sélecteur de photo de profil réutilisable (élèves, Mon compte, utilisateurs,
 * enseignants, personnel…) : import d'un fichier ou prise de vue par la caméra.
 * L'aperçu se met à jour immédiatement ; la photo est envoyée au serveur à
 * l'enregistrement du formulaire.
 */
export default function PhotoUploader({
  value,
  onChange,
  lastName,
  firstName,
  label = "Photo de profil",
  hint = "PNG, JPG ou WEBP · réduite automatiquement",
  size = "lg",
}: PhotoUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const src = value?.trim();

  const handleFile = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Format non pris en charge. Utilisez une image PNG, JPG ou WEBP.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("L'image est trop lourde (15 Mo maximum).");
      return;
    }
    setError(null);
    shrinkImage(file)
      .then(onChange)
      .catch(() => setError("Image illisible. Essayez une autre photo (PNG, JPG ou WEBP)."));
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0]);
    event.target.value = "";
  };

  return (
    <div>
      {label && <p className="mb-2 text-xs font-medium text-foreground-700">{label}</p>}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          aria-label="Importer une photo"
          className={`relative ${sizeClasses[size]} shrink-0 overflow-hidden rounded-full border border-background-200 bg-accent-100 cursor-pointer`}
        >
          {src ? (
            <img
              src={src}
              alt={`${firstName} ${lastName}`.trim() || "Photo de profil"}
              title={`${firstName} ${lastName}`.trim() || "Photo de profil"}
              className="h-full w-full object-cover object-top"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center font-semibold font-heading text-accent-800">
              {initials(lastName, firstName)}
            </span>
          )}
          <span
            className={`absolute inset-0 flex items-center justify-center bg-foreground-950/50 text-background-50 transition-opacity ${
              hover ? "opacity-100" : "opacity-0"
            }`}
          >
            <i className="ri-camera-line text-lg"></i>
          </span>
        </button>

        <div className="min-w-0">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              icon="ri-upload-2-line"
              onClick={() => inputRef.current?.click()}
            >
              Importer une photo
            </Button>
            <Button type="button" size="sm" variant="outline" icon="ri-camera-line" onClick={() => setCameraOpen(true)}>
              Prendre une photo
            </Button>
            {src && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                icon="ri-delete-bin-6-line"
                onClick={() => {
                  onChange("");
                  setError(null);
                }}
              >
                Retirer
              </Button>
            )}
          </div>
          <p className="mt-1.5 text-[11px] text-foreground-400">{hint}</p>
          {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
        </div>
      </div>

      <CameraCaptureModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(dataUrl) => {
          setError(null);
          onChange(dataUrl);
        }}
      />

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleInput}
      />
    </div>
  );
}